import { logger } from '@heidi/runtime/logger';
import { trackTemplateQualityGate } from '../../data/analytics/trackTemplateQualityGateAnalytics';
import {
  willTemplateSaveCreate,
  type TemplateSavePlan,
} from '../../data/templateSavePlan';
import type {
  TemplateQualityGateContext,
  TemplateQualityGateDecision,
} from './templateQualityGate.atoms';

export interface TemplateQualityGate {
  /** False outside UK/EU or when the kill switch is on; no save is ever gated. */
  isEnabled: boolean;
  /**
   * Whether this market mandates the attestation, before the kill switch is
   * applied. Separate from `isEnabled` so a suppressed save can be told from
   * one that was never in scope.
   */
  isInScope: boolean;
  /** The conditions this gate evaluates under, for reporting on its decisions. */
  context: TemplateQualityGateContext;
  /** Opens the gate dialog and suspends until the clinician decides. */
  requireAcceptance: (templateId?: string) => Promise<TemplateQualityGateDecision>;
}

/**
 * Resolves whether a save may proceed, prompting for the attestation first when
 * the save would create a template. False means the save must unwind.
 *
 * Outside the hook so the branching is unit-testable without rendering a
 * template editor, and so both builders share one decision.
 */
export const resolveTemplateQualityGate = async (
  gate: TemplateQualityGate,
  savePlan: TemplateSavePlan,
  templateId?: string
): Promise<boolean> => {
  if (!willTemplateSaveCreate(savePlan)) {
    return true;
  }

  const context = { ...gate.context, templateId };

  if (!gate.isEnabled) {
    // Only a save the kill switch suppressed is worth reporting — it means the
    // control is off in a market that mandates it. An out-of-region save was
    // never in scope, so reporting it would say nothing while outnumbering the
    // gated traffic several times over and burying the switch.
    //
    // `isInScope` inherits the fail-open of `useIsUKOrEU`, so a save made
    // before the region query settles counts as in scope wherever the user
    // really is. Read `bypassed` alongside `region_status: resolved` to get
    // the mandated markets only.
    if (gate.isInScope) {
      trackTemplateQualityGate('bypassed', context);
    }
    return true;
  }

  if (context.regionStatus === 'error') {
    logger.warn('template_quality_gate_region_lookup_failed', {
      surface: context.surface,
      templateId,
    });
  }

  const decision = await gate.requireAcceptance(templateId);
  trackTemplateQualityGate(decision, context);
  return decision === 'accepted';
};
